import { Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

interface ImagePreviewProps {
  file?: File | null;
  previewUrl?: string;
  existingImageUrl?: string;
  alt: string;
  width?: number;
  height?: number;
  showFileName?: boolean;
}

export const ImagePreview = ({
  file,
  previewUrl,
  existingImageUrl,
  alt,
  width = 150,
  height = 100,
  showFileName = true,
}: ImagePreviewProps) => {
  // Show both images side by side when replacing, or just one when not replacing
  const isReplacing = previewUrl && existingImageUrl;
  const shouldShowExisting = !previewUrl && existingImageUrl;

  return (
    <>
      {/* Show uploaded file name for reference */}
      {file && showFileName && (
        <Typography
          variant="caption"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 1 }}
        >
          {file.name}
        </Typography>
      )}

      {/* Show images side by side when replacing */}
      {isReplacing ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* New image preview */}
          <Box
            component="img"
            src={previewUrl}
            alt={`${alt} preview`}
            sx={{
              width,
              height,
              objectFit: 'contain',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
            }}
          />

          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={existingImageUrl}
              alt={`${alt} (will be replaced)`}
              sx={{
                width,
                height,
                objectFit: 'contain',
                border: '2px solid',
                borderColor: 'error.main',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                opacity: 0.7, // Make it slightly transparent to show it's being replaced
              }}
            />
            <IconButton
              size="small"
              sx={{
                'position': 'absolute',
                'top': 6,
                'right': 6,
                'backgroundColor': 'error.main',
                'color': 'white',
                '&:hover': {
                  backgroundColor: 'error.main',
                },
                'width': 24,
                'height': 24,
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <>
          {/* Show uploaded image preview only */}
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt={`${alt} preview`}
              sx={{
                width,
                height,
                objectFit: 'contain',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
              }}
            />
          )}

          {/* Show existing server image only if no new image uploaded */}
          {shouldShowExisting && (
            <Box
              component="img"
              src={existingImageUrl}
              alt={alt}
              sx={{
                width,
                height,
                objectFit: 'contain',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
              }}
            />
          )}
        </>
      )}
    </>
  );
};
